import { DatePicker, ToggleButton } from "@progress/kendo-react-dateinputs";
import { Checkbox, Input, NumericTextBox, TextArea } from "@progress/kendo-react-inputs";
import { Tooltip } from "@progress/kendo-react-tooltip";
import React from "react";
import CustomDropdown from "src/components/customDropdown";
import AddHospital from "./components/AddHospital";
import AddDivision from "./components/AddDivision";
import AddDepartment from "./components/AddDepartment";
import AddSpecialty from "./components/AddSpecialty";
import AddContractType from "./components/AddContractType";
import AddContractName from "./components/AddContractName";
import { transformDateTime } from "src/lib/date-time.util";
import {
  dummyDivisionData,
  dummyHospitalData,
  dummyDepartmentData,
  dummySpecialtyData,
  dummyFacilityData,
  dummyContractTypeData,
  dummyContractNameData,
  dummyTierData,
  dummyThresholdUnitTypeData,
  dummyRateTypeData,
  dummyRateUnitTypeData,
  dummyIsTransactionalData,
  dummyPeriodTypeData,
  dummyShiftData,
  dummyContractStructureData,
} from "./dummyData";

const CustomToggleButton = (props) => {
  return (
    <Tooltip
      position="bottom"
      anchorElement="target"
      style={{
        paddingTop: 10,
      }}
    >
      <ToggleButton {...props} title="Calendar" />
    </Tooltip>
  );
};

const ContractFields = (props) => {
  const [formFields, setFormFields] = React.useState({});
  const [errorObj, setErrorObj] = React.useState({});
  const [filteredDropdownData, setFilteredDropdownData] = React.useState({});
  const [showPopup, setShowPopup] = React.useState({
    showHospitalPopup: false,
    showDivisionPopup: false,
    showDepartmentPopup: false,
    showSpecialtyPopup: false,
    showContractTypePopup: false,
    showContractNamePopup: false,
  });

  let {
    dataItem,
    selected,
    isFieldDisabled,
    organizationalData,
    contractData,
    onOrganizationalDataChange,
    onContractDataChange,
    dropdownData: propDropdownData,
    validationErrors = {},
    onRefreshDropdownData,
    ...others
  } = props;

  // Reset filtered data when dropdownData changes
  React.useEffect(() => {
    setFilteredDropdownData({});
  }, [propDropdownData]);
  let fItem = dataItem;

  // Hide Department and Facility dropdowns across add/update forms
  if (fItem?.filter_column_name === "department" || fItem?.filter_column_name === "facility") {
    return null;
  }

  /**
   * Determine if the current field should be disabled based on sequential validation
   * This is controlled by the parent component's isFieldDisabled function
   * which enforces the order: Division -> Hospital -> Specialty -> Contract Type -> Contract Name
   */
  const isDisabled = isFieldDisabled ? isFieldDisabled(fItem.filter_column_name) : false;

  // Get the current value for this field from parent state
  const getCurrentValue = () => {
    const fieldName = fItem.filter_column_name;

    if (
      ["division", "hospital", "department", "specialty", "facility", "employeeId", "employeeName"].includes(fieldName)
    ) {
      return organizationalData?.[fieldName] || "";
    } else if (
      ["contractType", "contractName", "contractStartDate", "contractEndDate", "contractStructure"].includes(fieldName)
    ) {
      return contractData?.[fieldName] || "";
    }

    return "";
  };

  // Get the current value for this field
  const currentValue = getCurrentValue();

  // Update local form fields and parent component state
  const updateFieldValue = (fieldName, value, parentCallback) => {
    // Update local state
    setFormFields({
      ...formFields,
      [fieldName]: value,
    });

    // Update parent component state if callback is provided
    if (parentCallback) {
      parentCallback(fieldName, value);
    }

    // Clear error for this field
    setErrorObj({
      ...errorObj,
      [fItem.filter_label]: "",
    });
  };

  // Dropdown data mapping - use prop data if available, otherwise fallback to dummy data
  const dropdownData = {
    // Organizational Attributes
    division: propDropdownData?.divisions || dummyDivisionData,
    hospital: propDropdownData?.hospitals || dummyHospitalData,
    hospital_location: propDropdownData?.hospitals || dummyHospitalData,
    department: propDropdownData?.departments || dummyDepartmentData,
    specialty: propDropdownData?.specialties || dummySpecialtyData,
    facility: propDropdownData?.facilities || dummyFacilityData,

    // Contract Attributes
    contractType: propDropdownData?.contractTypes || dummyContractTypeData,
    contractName: propDropdownData?.contractNames || dummyContractNameData,

    // Compensation Attributes
    tier: dummyTierData,
    threshold_unit_type: dummyThresholdUnitTypeData,
    rate_type: dummyRateTypeData,
    rate_unit_type: dummyRateUnitTypeData,
    is_transactional: dummyIsTransactionalData,
    period_type: dummyPeriodTypeData,
    shift: dummyShiftData,
    contractStructure: dummyContractStructureData,
  };

  const handleFieldChange = (e, fItem) => {
    const name = fItem.filter_column_name;
    const value = e.value ? e.value : e.target.value;

    // Determine which parent callback to use based on field name
    const isOrganizationalField = [
      "division",
      "hospital",
      "department",
      "specialty",
      "facility",
      "employeeId",
      "employeeName",
    ].includes(name);
    const isContractField = [
      "contractType",
      "contractName",
      "contractStartDate",
      "contractEndDate",
      "contractStructure",
    ].includes(name);

    let parentCallback = null;
    if (isOrganizationalField && onOrganizationalDataChange) {
      parentCallback = onOrganizationalDataChange;
    } else if (isContractField && onContractDataChange) {
      parentCallback = onContractDataChange;
    }

    updateFieldValue(name, value, parentCallback);
  };

  const handleNumberFieldChange = (e, fItem) => {
    const name = fItem.filter_column_name;
    const value = typeof e?.target?.value === "number" ? e?.target?.value : "";

    // Determine which parent callback to use based on field name
    const isOrganizationalField = [
      "division",
      "hospital",
      "department",
      "specialty",
      "facility",
      "employeeId",
      "employeeName",
    ].includes(name);
    const isContractField = [
      "contractType",
      "contractName",
      "contractStartDate",
      "contractEndDate",
      "contractStructure",
    ].includes(name);

    let parentCallback = null;
    if (isOrganizationalField && onOrganizationalDataChange) {
      parentCallback = onOrganizationalDataChange;
    } else if (isContractField && onContractDataChange) {
      parentCallback = onContractDataChange;
    }

    updateFieldValue(name, value, parentCallback);
  };

  const handleDropdownChange = (e, fItem) => {
    const name = fItem.filter_column_name;
    const value = e;

    // Determine which parent callback to use based on field name
    const isOrganizationalField = [
      "division",
      "hospital",
      "department",
      "specialty",
      "facility",
      "employeeId",
      "employeeName",
    ].includes(name);
    const isContractField = [
      "contractType",
      "contractName",
      "contractStartDate",
      "contractEndDate",
      "contractStructure",
    ].includes(name);

    let parentCallback = null;
    if (isOrganizationalField && onOrganizationalDataChange) {
      parentCallback = onOrganizationalDataChange;
    } else if (isContractField && onContractDataChange) {
      parentCallback = onContractDataChange;
    }

    updateFieldValue(name, value, parentCallback);
  };

  const getDropdownValue = (fItem) => {
    const name = fItem.filter_column_name;
    const value = currentValue;

    if (!value) return "";

    // If value is already an object with text property (from dropdown selection)
    if (typeof value === "object" && value.text) {
      return value;
    }

    // If value is a string (from parent state), find the matching dropdown item
    if (typeof value === "string") {
      const foundItem = dropdownData[name]?.find((item) => item.text === value);
      return foundItem || "";
    }

    return "";
  };

  // Helper function to determine if plus icon should be shown
  const shouldShowPlusIcon = (fieldName) => {
    const fieldsWithPlusIcon = [
      "hospital",
      "hospital_location",
      "division",
      "specialty",
      "contractType",
      "contractName",
    ];
    return fieldsWithPlusIcon.includes(fieldName);
  };

  // Helper function to handle plus icon clicks
  const handlePlusIconClick = (fieldName) => {
    const popupMap = {
      hospital: "showHospitalPopup",
      hospital_location: "showHospitalPopup",
      division: "showDivisionPopup",
      department: "showDepartmentPopup",
      specialty: "showSpecialtyPopup",
      contractType: "showContractTypePopup",
      contractName: "showContractNamePopup",
    };

    const popupKey = popupMap[fieldName];
    if (popupKey) {
      setShowPopup({ ...showPopup, [popupKey]: true });
    }
  };

  // Helper function to get the appropriate data array for a field
  const getDataArrayForField = (fieldName) => {
    const dataMap = {
      hospital: "dummyHospitalData",
      hospital_location: "dummyHospitalData",
      division: "dummyDivisionData",
      department: "dummyDepartmentData",
      specialty: "dummySpecialtyData",
      contractType: "dummyContractTypeData",
      contractName: "dummyContractNameData",
    };

    return dataMap[fieldName];
  };

  // Helper function to get original dropdown data (without filtering)
  const getOriginalDropdownData = (fItem) => {
    const name = fItem.filter_column_name;

    // Check for exact match first
    if (dropdownData[name]) {
      return dropdownData[name];
    }

    // Check for dynamic field names (e.g., contract_type_1, tier_1, etc.)
    const baseFieldName = name.replace(/_\d+$/, ""); // Remove trailing _number
    if (dropdownData[baseFieldName]) {
      return dropdownData[baseFieldName];
    }

    return null;
  };

  // Helper function to get dropdown data for both static and dynamic field names
  const getDropdownData = (fItem) => {
    const name = fItem.filter_column_name;

    // Return filtered data if available
    if (filteredDropdownData[name]) {
      return filteredDropdownData[name];
    }

    // Check for exact match first
    if (dropdownData[name]) {
      return dropdownData[name];
    }

    // Check for dynamic field names (e.g., contract_type_1, tier_1, etc.)
    const baseFieldName = name.replace(/_\d+$/, ""); // Remove trailing _number
    if (dropdownData[baseFieldName]) {
      return dropdownData[baseFieldName];
    }

    return null;
  };

  const handleDropdownFilterChange = (filteredData, fieldName) => {
    setFilteredDropdownData((prev) => ({
      ...prev,
      [fieldName]: filteredData,
    }));
  };

  const renderDocumentUpload = () => {
    return (
      <div className="document-upload-section">
        <div className="upload-area">
          <div className="upload-placeholder">
            <i className="fas fa-cloud-upload-alt"></i>
            <p>Attach New Document(s)</p>
          </div>
        </div>
        <div className="uploaded-documents">
          <div className="document-item">
            <i className="fas fa-file"></i>
            <div className="document-info">
              <span className="document-name">Test_Document.docx</span>
              <span className="document-size">30 kb</span>
            </div>
            <i className="fas fa-times delete-doc"></i>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <li {...others} selected={props.selected} className={`${props.className} ${isDisabled ? "disabled-field" : ""}`}>
        <div className="fullwidthelm">
          <label className={`provider-elm ${isDisabled ? "disabled-label" : ""}`}>
            {fItem.filter_label}
            {fItem.is_nullable === 0 && "*"}
          </label>

          {fItem.filter_column_datatype === "date" ? (
            <>
              <DatePicker
                value={(() => {
                  const value = currentValue;
                  if (value) {
                    return typeof value === "object"
                      ? value
                      : transformDateTime({
                          date: value,
                          includeTime: true,
                          defaultTime: true,
                          returnDate: true,
                        });
                  }
                  return value;
                })()}
                name={fItem.filter_column_name}
                format="yyyy-MM-dd"
                className="provider-elm calendariconprovider"
                onChange={(e) => handleFieldChange(e, fItem)}
                formatPlaceholder={{
                  year: "yyyy",
                  month: "mm",
                  day: "dd",
                }}
                placeholder="yyyy-mm-dd"
                toggleButton={CustomToggleButton}
                disabled={isDisabled}
              />
              <p className="error-msg">{validationErrors[fItem.filter_column_name] || errorObj[fItem.filter_label]}</p>
            </>
          ) : fItem.filter_column_datatype === "number" ? (
            <>
              {fItem.filter_column_name === "min_threshold" || fItem.filter_column_name === "max_threshold" ? (
                <div className="threshold-inputs">
                  <NumericTextBox
                    value={currentValue}
                    className="provider-elm threshold-input"
                    format={"#.########"}
                    onChange={(e) => handleNumberFieldChange(e, fItem)}
                    placeholder={fItem.filter_column_name === "min_threshold" ? "Min" : "Max"}
                    disabled={isDisabled}
                  />
                </div>
              ) : (
                <div className="input-with-prefix">
                  {fItem.filter_column_name === "annual_comp_cap" && <span className="input-prefix">$</span>}
                  <NumericTextBox
                    value={currentValue}
                    className={`provider-elm ${fItem.filter_column_name === "annual_comp_cap" ? "with-prefix" : ""}`}
                    format={"#.########"}
                    onChange={(e) => handleNumberFieldChange(e, fItem)}
                    disabled={isDisabled}
                  />
                </div>
              )}
              <p className="error-msg">{validationErrors[fItem.filter_column_name] || errorObj[fItem.filter_label]}</p>
            </>
          ) : fItem.filter_column_datatype === "doc" ? (
            renderDocumentUpload()
          ) : getDropdownData(fItem) ? (
            <>
              <div
                className={`provider-elm ${shouldShowPlusIcon(fItem.filter_column_name) ? "pluswidget updateprodrpdwn" : ""}`}
              >
                <CustomDropdown
                  data={getDropdownData(fItem)}
                  dataList={getOriginalDropdownData(fItem)}
                  textField="text"
                  dataItemKey="id"
                  className="provider-elm"
                  valueData={getDropdownValue(fItem)}
                  onChange={(e) => handleDropdownChange(e, fItem)}
                  onFilterChange={(filteredData) => handleDropdownFilterChange(filteredData, fItem.filter_column_name)}
                  filterable={true}
                  autoClose={false}
                  autoBind={true}
                  disabled={isDisabled}
                />
                {shouldShowPlusIcon(fItem.filter_column_name) && (
                  <i
                    className={`fas fa-plus ${isDisabled ? "disabled-icon" : ""}`}
                    onClick={() => !isDisabled && handlePlusIconClick(fItem.filter_column_name)}
                    style={{
                      cursor: isDisabled ? "not-allowed" : "pointer",
                      opacity: isDisabled ? 0.5 : 1,
                    }}
                  ></i>
                )}
              </div>
              <p className="error-msg">{validationErrors[fItem.filter_column_name] || errorObj[fItem.filter_label]}</p>
            </>
          ) : (
            <>
              <Input
                type="text"
                className="provider-elm"
                value={currentValue}
                onChange={(e) => handleFieldChange(e, fItem)}
                disabled={isDisabled}
              />
              <p className="error-msg">{validationErrors[fItem.filter_column_name] || errorObj[fItem.filter_label]}</p>
            </>
          )}
        </div>
      </li>
      {/* Hospital Modal */}
      {showPopup.showHospitalPopup && (
        <AddHospital
          isVisible={showPopup.showHospitalPopup}
          onClose={() => setShowPopup({ ...showPopup, showHospitalPopup: false })}
          breadcrumbData={{
            division: organizationalData?.division || "",
          }}
          onSave={async (newHospital) => {
            // Optimistically add into both dropdowns if visible in this list
            if (dummyHospitalData && Array.isArray(dummyHospitalData)) {
              // Avoid duplicates
              if (!dummyHospitalData.find((d) => d.text === newHospital.text)) {
                dummyHospitalData.push(newHospital);
              }
            }
            // If current field is hospital/hospital_location, set selection
            const name = fItem.filter_column_name;
            if (name === "hospital" || name === "hospital_location") {
              setFormFields({ ...formFields, [name]: newHospital });
            }

            // Refresh the location dropdown data from the database and auto-select the newly added item
            if (onRefreshDropdownData) {
              await onRefreshDropdownData("hospital", newHospital);
            }
          }}
        />
      )}

      {/* Division Modal */}
      {showPopup.showDivisionPopup && (
        <AddDivision
          isVisible={showPopup.showDivisionPopup}
          onClose={() => setShowPopup({ ...showPopup, showDivisionPopup: false })}
          onSave={async (newDivision) => {
            if (dummyDivisionData && Array.isArray(dummyDivisionData)) {
              if (!dummyDivisionData.find((d) => d.text === newDivision.text)) {
                dummyDivisionData.push(newDivision);
              }
            }
            const name = fItem.filter_column_name;
            if (name === "division") {
              setFormFields({ ...formFields, [name]: newDivision });
            }

            // Refresh the division dropdown data from the database and auto-select the newly added item
            if (onRefreshDropdownData) {
              await onRefreshDropdownData("division", newDivision);
            }
          }}
        />
      )}

      {/* Department Modal */}
      {/* {showPopup.showDepartmentPopup && (
            <AddDepartment
                isVisible={showPopup.showDepartmentPopup}
                onClose={() => setShowPopup({ ...showPopup, showDepartmentPopup: false })}
                breadcrumbData={{
                    division: organizationalData?.division || "",
                    hospital: organizationalData?.hospital || ""
                }}
                onSave={(newDepartment) => {
                    if (dummyDepartmentData && Array.isArray(dummyDepartmentData)) {
                        if (!dummyDepartmentData.find((d) => d.text === newDepartment.text)) {
                            dummyDepartmentData.push(newDepartment);
                        }
                    }
                    const name = fItem.filter_column_name;
                    if (name === 'department') {
                        setFormFields({ ...formFields, [name]: newDepartment });
                    }
                }}
            />
        )} */}

      {/* Specialty Modal */}
      {showPopup.showSpecialtyPopup && (
        <AddSpecialty
          isVisible={showPopup.showSpecialtyPopup}
          onClose={() => setShowPopup({ ...showPopup, showSpecialtyPopup: false })}
          breadcrumbData={{
            division: organizationalData?.division || "",
            hospital: organizationalData?.hospital || "",
          }}
          onSave={async (newSpecialty) => {
            if (dummySpecialtyData && Array.isArray(dummySpecialtyData)) {
              if (!dummySpecialtyData.find((d) => d.text === newSpecialty.text)) {
                dummySpecialtyData.push(newSpecialty);
              }
            }
            const name = fItem.filter_column_name;
            if (name === "specialty") {
              setFormFields({ ...formFields, [name]: newSpecialty });
            }

            // Refresh the specialty dropdown data from the database and auto-select the newly added item
            if (onRefreshDropdownData) {
              await onRefreshDropdownData("specialty", newSpecialty);
            }
          }}
        />
      )}

      {/* Contract Type Modal */}
      {showPopup.showContractTypePopup && (
        <AddContractType
          isVisible={showPopup.showContractTypePopup}
          onClose={() => setShowPopup({ ...showPopup, showContractTypePopup: false })}
          breadcrumbData={{
            division: organizationalData?.division || "",
            hospital: organizationalData?.hospital || "",
            specialty: organizationalData?.specialty || "",
          }}
          onSave={async (newContractType) => {
            if (dummyContractTypeData && Array.isArray(dummyContractTypeData)) {
              if (!dummyContractTypeData.find((d) => d.text === newContractType.text)) {
                dummyContractTypeData.push(newContractType);
              }
            }
            const name = fItem.filter_column_name;
            if (name === "contractType") {
              setFormFields({ ...formFields, [name]: newContractType });
            }

            // Refresh the contract type dropdown data from the database and auto-select the newly added item
            if (onRefreshDropdownData) {
              await onRefreshDropdownData("contractType", newContractType);
            }
          }}
        />
      )}

      {/* Contract Name Modal */}
      {showPopup.showContractNamePopup && (
        <AddContractName
          isVisible={showPopup.showContractNamePopup}
          onClose={() => setShowPopup({ ...showPopup, showContractNamePopup: false })}
          breadcrumbData={{
            division: organizationalData?.division || "",
            hospital: organizationalData?.hospital || "",
            specialty: organizationalData?.specialty || "",
            contractType: contractData?.contractType || "",
          }}
          onSave={async (newContractName) => {
            if (dummyContractNameData && Array.isArray(dummyContractNameData)) {
              if (!dummyContractNameData.find((d) => d.text === newContractName.text)) {
                dummyContractNameData.push(newContractName);
              }
            }
            const name = fItem.filter_column_name;
            if (name === "contractName") {
              setFormFields({ ...formFields, [name]: newContractName });
            }

            // Refresh the contract name dropdown data from the database and auto-select the newly added item
            if (onRefreshDropdownData) {
              await onRefreshDropdownData("contractName", newContractName);
            }
          }}
        />
      )}
    </>
  );
};

export default React.memo(ContractFields);
